import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  TreePine, 
  User, 
  Calendar, 
  MapPin, 
  ExternalLink,
  Loader2,
  Link as LinkIcon,
  ChevronRight,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  searchFamilySearchRecords, 
  linkGenealogyToCase, 
  getCaseGenealogyLinks,
  removeGenealogyLink,
  getFamilySearchStatus 
} from '../lib/api';

const CaseGenealogySection = ({ caseId, caseName, caseData }) => {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [linkedRecords, setLinkedRecords] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [linkForm, setLinkForm] = useState({
    relationship: 'self',
    notes: ''
  });
  const [apiConfigured, setApiConfigured] = useState(true);

  // Pre-populate search with case name
  const [searchQuery, setSearchQuery] = useState(caseName || '');

  useEffect(() => {
    checkApiStatus();
    loadLinkedRecords();
  }, [caseId]);

  const checkApiStatus = async () => {
    try {
      const status = await getFamilySearchStatus();
      setApiConfigured(status.configured);
    } catch (err) {
      console.error('Failed to check FS status:', err);
    }
  };

  const loadLinkedRecords = async () => {
    try {
      const response = await getCaseGenealogyLinks(caseId);
      setLinkedRecords(response.links || []);
    } catch (err) {
      console.error('Failed to load genealogy links:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    try {
      // Extract name parts for better search
      const nameParts = searchQuery.trim().split(' ');
      const searchParams = {
        name: searchQuery,
        count: 10
      };

      // Add birth year if available from case
      if (caseData?.age && caseData?.last_seen_date) {
        const lastSeenYear = new Date(caseData.last_seen_date).getFullYear();
        const birthYear = lastSeenYear - caseData.age;
        searchParams.birth_year = String(birthYear);
      }

      // Add location if available
      if (caseData?.state) {
        searchParams.birth_place = caseData.state;
      }

      const response = await searchFamilySearchRecords(searchParams);
      
      if (response.success) {
        setResults(response.results);
        if (response.results.length === 0) {
          toast.info('No genealogy records found. Try adjusting the search.');
        }
      } else {
        toast.error(response.message || 'Search failed');
      }
    } catch (err) {
      toast.error('Failed to search genealogy records');
    } finally {
      setSearchLoading(false);
    }
  };

  const openLinkDialog = (result) => {
    setSelectedResult(result);
    setShowLinkDialog(true);
  };

  const handleLinkRecord = async () => {
    if (!selectedResult) return;
    
    setLoading(true);
    try {
      await linkGenealogyToCase({
        case_id: caseId,
        familysearch_id: selectedResult.id,
        familysearch_name: selectedResult.name,
        relationship: linkForm.relationship,
        notes: linkForm.notes
      });

      toast.success(`Linked ${selectedResult.name} to this case`);
      setShowLinkDialog(false);
      setLinkForm({ relationship: 'self', notes: '' });
      loadLinkedRecords();
    } catch (err) {
      toast.error('Failed to link record');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkRecord = async (linkId) => {
    try {
      await removeGenealogyLink(linkId);
      toast.success('Genealogy link removed');
      loadLinkedRecords();
    } catch (err) {
      toast.error('Failed to remove link');
    }
  };

  const relationshipLabels = {
    self: 'May be this person',
    parent: 'Parent',
    sibling: 'Sibling',
    child: 'Child',
    spouse: 'Spouse',
    grandparent: 'Grandparent',
    other: 'Other relative'
  };

  return (
    <Card className="border-emerald-200 dark:border-emerald-800">
      <CardHeader className="bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900">
        <CardTitle className="font-heading flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
          <TreePine className="w-5 h-5" />
          Genealogy Records
        </CardTitle>
        <CardDescription>
          Search FamilySearch's database of historical records to find potential connections
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {/* Coming Soon Message */}
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
            <TreePine className="w-8 h-8 text-emerald-500" />
          </div>
          <h3 className="text-lg font-semibold text-emerald-700 dark:text-emerald-400 mb-2">
            Coming Soon
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            We're working on integrating genealogy search to help find potential family connections through historical records.
          </p>
          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
            In Development
          </Badge>
        </div>

        {/* Hidden: Original functionality - uncomment when API is ready
        {!apiConfigured && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">FamilySearch API Not Configured</p>
              <p className="text-amber-700">
                <a href="https://www.familysearch.org/developers/" target="_blank" rel="noopener noreferrer" className="underline">
                  Register for API access
                </a> to enable genealogy search.
              </p>
            </div>
          </div>
        )}
        */}

        {/* Linked Records - Keep this for when feature is ready */}
        {linkedRecords.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Linked Records ({linkedRecords.length})
            </h4>
            <div className="space-y-2">
              {linkedRecords.map((link) => (
                <div key={link.id} className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-emerald-600" />
                    <div>
                      <span className="font-medium">{link.familysearch_name}</span>
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {relationshipLabels[link.relationship] || link.relationship}
                      </Badge>
                      {link.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{link.notes}</p>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleUnlinkRecord(link.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Section - Hidden until API is configured */}
        {apiConfigured && showSearch ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button 
                onClick={handleSearch}
                disabled={searchLoading || !searchQuery.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {searchLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
              <Button variant="outline" onClick={() => setShowSearch(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Results */}
            {results.length > 0 && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {results.map((result, idx) => (
                  <div 
                    key={result.id || idx}
                    className="p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-emerald-600" />
                          <span className="font-medium">{result.name}</span>
                          {result.score > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              {Math.round(result.score * 100)}%
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                          {result.birth_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Born: {result.birth_date}
                            </span>
                          )}
                          {result.birth_place && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {result.birth_place}
                            </span>
                          )}
                          {result.record_type && (
                            <Badge variant="outline" className="text-xs">
                              {result.record_type}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {result.record_url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={result.record_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openLinkDialog(result)}
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        >
                          <LinkIcon className="w-4 h-4 mr-1" />
                          Link
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : apiConfigured ? (
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => setShowSearch(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Search className="w-4 h-4 mr-2" />
              Search Genealogy Records
            </Button>
            <Button variant="outline" asChild>
              <Link to="/genealogy">
                <TreePine className="w-4 h-4 mr-2" />
                Advanced Search
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        ) : null}

        {/* Link Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Link Genealogy Record</DialogTitle>
              <DialogDescription>
                Connect this genealogy record to the missing person case.
              </DialogDescription>
            </DialogHeader>
            
            {selectedResult && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium">{selectedResult.name}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedResult.birth_date && <span>Born: {selectedResult.birth_date}</span>}
                    {selectedResult.birth_place && <span> • {selectedResult.birth_place}</span>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="relationship">Relationship to Missing Person</Label>
                  <Select 
                    value={linkForm.relationship}
                    onValueChange={(value) => setLinkForm(prev => ({ ...prev, relationship: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">May be this person</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="sibling">Sibling</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                      <SelectItem value="spouse">Spouse</SelectItem>
                      <SelectItem value="grandparent">Grandparent</SelectItem>
                      <SelectItem value="other">Other relative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any relevant notes about this connection..."
                    value={linkForm.notes}
                    onChange={(e) => setLinkForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleLinkRecord}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Link Record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CaseGenealogySection;
